/**
 * Expand a possibly-range code string into the flat list of codes it represents.
 *
 * Recognized shapes:
 *   - Single literal       "96523"            → ["96523"]
 *   - Numeric range        "0272-0372"        → ["0272", …, "0372"]   (padded to endpoint width)
 *   - Single-letter HCPCS  "J1642-J1700"      → ["J1642", …, "J1700"]
 *
 * Malformed / mismatched / backwards inputs pass through as the literal string —
 * the backend does string equality, so an unrecognized token simply won't match.
 */
export function expandRange(input: string): string[] {
  const trimmed = input.trim();
  if (!trimmed) return [];
  if (!trimmed.includes("-")) return [trimmed];

  const parts = trimmed.split("-");
  if (parts.length !== 2) return [trimmed];

  const lo = parts[0].trim();
  const hi = parts[1].trim();
  if (!lo || !hi) return [trimmed];
  // Both endpoints must be the same width — "1-100" is ambiguous, so we pass through.
  if (lo.length !== hi.length) return [trimmed];
  if (lo > hi) return [trimmed];

  // Numeric range (DRGs like "001-999", numeric CPTs like "0272-0372").
  if (/^\d+$/.test(lo) && /^\d+$/.test(hi)) {
    const padLen = lo.length;
    const start = parseInt(lo, 10);
    const end = parseInt(hi, 10);
    const result: string[] = [];
    for (let n = start; n <= end; n += 1) {
      result.push(String(n).padStart(padLen, "0"));
    }
    return result;
  }

  // Alphanumeric range (HCPCS Level II — "J1642-J1700", "A0021-A0030").
  const loMatch = lo.match(/^([A-Z])(\d+)$/);
  const hiMatch = hi.match(/^([A-Z])(\d+)$/);
  if (loMatch && hiMatch && loMatch[1] === hiMatch[1]) {
    const prefix = loMatch[1];
    const padLen = loMatch[2].length;
    const start = parseInt(loMatch[2], 10);
    const end = parseInt(hiMatch[2], 10);
    const result: string[] = [];
    for (let n = start; n <= end; n += 1) {
      result.push(`${prefix}${String(n).padStart(padLen, "0")}`);
    }
    return result;
  }

  return [trimmed];
}

export interface IPrepareCodesResult {
  codes: string[];
  /** Codes (post-expansion) longer than the sanity cap — surfaced to the user. */
  oversized: string[];
}

/** Per spec: any individual range that expands to more than this is rejected. */
export const RANGE_EXPANSION_LIMIT = 1000;
/** Per spec: post-expansion no code may be longer than this. */
export const CODE_MAX_LENGTH = 32;

export class RangeTooLargeError extends Error {
  constructor(public readonly token: string, public readonly size: number) {
    super(`Range too large — please narrow it ("${token}" → ${size} codes)`);
    this.name = "RangeTooLargeError";
  }
}

/**
 * Normalize the chip list before sending to the backend.
 * - splits any embedded whitespace / comma so a pasted column lands as separate tokens
 * - expands ranges to flat lists (throws RangeTooLargeError if any single range > limit)
 * - drops codes that exceed the sanity length cap
 * - dedupes (preserves first-seen order)
 */
export function prepareCodesForSubmit(chips: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  chips.forEach((raw) => {
    String(raw)
      .split(/[\s,]+/)
      .forEach((token) => {
        if (!token) return;
        const expanded = expandRange(token);
        if (expanded.length > RANGE_EXPANSION_LIMIT) {
          throw new RangeTooLargeError(token, expanded.length);
        }
        expanded.forEach((code) => {
          if (!code) return;
          if (code.length > CODE_MAX_LENGTH) return;
          if (seen.has(code)) return;
          seen.add(code);
          result.push(code);
        });
      });
  });

  return result;
}

/** Intersection of two code lists, in the order they appear in the first list. */
export function intersectCodes(a: string[], b: string[]): string[] {
  if (a.length === 0 || b.length === 0) return [];
  const bSet = new Set(b);
  return a.filter((code) => bSet.has(code));
}
